import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import z from 'zod'

import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SelectOption } from '@/components/ui/select'
import { TableColumn } from '@/components/ui/table'
import { SortOrderSchema } from '@/components/ui/table/table.types'
import { EMPTY_VALUE } from '@/constants'
import { Checklist, ChecklistType, ChecklistTypeEnum } from '@/models/features'
import { formatDateLong } from '@/utils/date'

import { ChecklistStatusExtendedEnum } from './checklists-dashboard.page.types'
import {
  getChecklistStatusColor,
  getChecklistStatusLabel,
  getChecklistTypeColor,
  getChecklistTypeLabel,
} from './checklists-dashboard.page.utils'

export const CHECKLISTS_DASHBOARD_ID = 'checklists-dashboard'

export const ADDITIONAL_FILTERS_DROPDOWN_WIDTH = '32.8rem'

export const makeChecklistTypeOptions = (
  t: TFunction<['checklists']>,
): SelectOption<ChecklistType>[] => [
  {
    label: getChecklistTypeLabel(ChecklistTypeEnum.enum['task'], t),
    value: ChecklistTypeEnum.enum['task'],
  },
  {
    label: getChecklistTypeLabel(ChecklistTypeEnum.enum['inspection'], t),
    value: ChecklistTypeEnum.enum['inspection'],
  },
]

interface GetChecklistsColumnsProps {
  timezone: string
  t: TFunction<['checklists']>
  residentColumnSettings: PersonColumnSettings
}

export const getChecklistsColumns = ({
  timezone,
  t,
  residentColumnSettings,
}: GetChecklistsColumnsProps): TableColumn<Checklist>[] => [
  {
    id: 'name',
    render: (checklist) => checklist.name,
    sortKey: 'name',
    title: t('checklists:dashboard.columns.name'),
    width: '28rem',
  },
  {
    id: 'status',
    render: (checklist) => (
      <Badge color={getChecklistStatusColor(checklist.status)} variant="light" size="small">
        {getChecklistStatusLabel(checklist.status, t)}
      </Badge>
    ),
    title: t('checklists:dashboard.columns.status'),
  },
  {
    id: 'dueDate',
    render: (checklist) =>
      checklist.dueDate ? formatDateLong(toZonedTime(checklist.dueDate, timezone)) : EMPTY_VALUE,
    sortKey: 'dueDate',
    title: t('checklists:dashboard.columns.dueDate'),
  },
  {
    id: 'type',
    render: (checklist) => (
      <Badge color={getChecklistTypeColor(checklist.type)} variant="light" size="small">
        {getChecklistTypeLabel(checklist.type, t)}
      </Badge>
    ),
    title: t('checklists:dashboard.columns.type'),
  },
  getPersonColumn(residentColumnSettings, {
    getDisplay: (row) => row.residentDisplay,
    getPerson: (checklist) =>
      checklist.clientName || checklist.firstName
        ? {
            apartment: checklist.unit,
            avatar: checklist.avatar,
            buildingNumber: checklist.buildingNumber,
            clientId: checklist.clientId,
            email: checklist.email,
            firstName: checklist.firstName ?? checklist.clientName,
            kind: 'resident',
            lastName: checklist.firstName ? checklist.lastName : null,
            phone: checklist.phone,
            street: checklist.street,
          }
        : null,
    id: 'resident',
    pageName: t('checklists:dashboard.pageName'),
    sortKey: 'clientName',
    source: 'checklists',
    title: t('checklists:dashboard.columns.resident'),
  }),
  {
    id: 'unitNumber',
    render: (checklist) => checklist.unit,
    title: t('checklists:dashboard.columns.unitNumber'),
  },
  {
    id: 'moveIn',
    render: (checklist) =>
      checklist.moveInDate
        ? formatDateLong(toZonedTime(checklist.moveInDate, timezone))
        : EMPTY_VALUE,
    sortKey: 'moveInDate',
    title: t('checklists:dashboard.columns.moveIn'),
  },
]

export const checklistsQueryFiltersSchema = z.object({
  clientId: z.array(z.coerce.number()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  parentChecklistId: z.array(z.coerce.number()).optional(),
  search: z.coerce.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  status: ChecklistStatusExtendedEnum.optional(),
  toDate: z.coerce.number().optional(),
  type: ChecklistTypeEnum.optional(),
})

export type ChecklistsFilters = z.infer<typeof checklistsQueryFiltersSchema>

export const checklistsInitialFilters: ChecklistsFilters = {
  clientId: [],
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  parentChecklistId: [],
  search: '',
  skip: 0,
  sortBy: undefined,
  sortOrder: undefined,
  status: ChecklistStatusExtendedEnum.enum['all'],
  toDate: undefined,
  type: undefined,
}
